import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import LoadingButton from "../components/LoadingButton";
import ChangePasswordModal from "../components/ChangePasswordModal";
import { motion, AnimatePresence } from "framer-motion";
import { FiMail, FiLock, FiAlertCircle } from "react-icons/fi";

export default function Login() {
  const { login, refreshUser, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (user?.force_password_change) {
      setShowChangePasswordModal(true);
    }
  }, [user?.force_password_change]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await login(email, password);
      console.log("Login.handleSubmit: login response", {
        hasToken: !!response?.token,
        user: response?.user,
      });
      if (response.token) {
        if (response.user?.force_password_change) {
          // Show modal to change password
          setShowChangePasswordModal(true);
          return;
        }
        // Priorizar onboarding_completed sobre la presencia de phones
        if (response.user?.onboarding_completed) {
          navigate("/");
        } else {
          navigate("/onboarding");
        }
      } else {
        setError(response.message || t("login.error_invalid"));
      }
    } catch (err) {
      console.error("Login error:", err);
      const errorData = err.response?.data;

      if (errorData?.error_code === "PHONE_NOT_VERIFIED") {
        navigate("/verify-phone", {
          state: {
            user_id: errorData.user_id,
            phone: errorData.phone,
            email: email,
          },
        });
        return;
      }

      const message = errorData?.message || t("login.error_generic");
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChanged = async () => {
    setShowChangePasswordModal(false);
    try {
      // Refresh user data to get updated force_password_change flag
      await refreshUser();
      // Navigate based on user status
      const user = JSON.parse(localStorage.getItem("wascaner_session"));
      // Priorizar onboarding_completed sobre la presencia de phones
      if (user?.onboarding_completed) {
        navigate("/");
      } else {
        navigate("/onboarding");
      }
    } catch (error) {
      console.error("Failed to refresh user after password change:", error);
      // Still navigate even if refresh fails
      navigate("/");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-gray-50">
      {/* Background Blobs */}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] bg-primary-200/30 rounded-full blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] bg-emerald-200/30 rounded-full blur-[120px]" />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        className="w-full max-w-md relative z-10"
      >
        <div className="bg-white/70 backdrop-blur-xl border border-white/60 p-8 sm:p-10 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-500">
          {/* Header */}
          <div className="text-center mb-10">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="bg-gradient-to-br from-primary-50 to-emerald-50 rounded-2xl p-4 w-auto h-20 mx-auto mb-6 shadow-inner border border-white flex items-center justify-center group"
            >
              <img
                src="/WhappScan.png"
                alt="WhappScan"
                className="h-10 w-auto group-hover:scale-110 transition-transform duration-300"
              />
            </motion.div>
            <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight mb-2">
              {t("app.signin")}
            </h1>
            <p className="text-gray-500 text-base font-medium">
              {t("app.welcome")}
            </p>
          </div>

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                <div className="p-4 bg-red-50/80 border border-red-100 text-red-600 rounded-xl text-sm font-medium flex items-center gap-2">
                  <FiAlertCircle className="w-5 h-5 flex-shrink-0" />
                  {error}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              {/* Email Field */}
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiMail className="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                </div>
                <input
                  type="email"
                  className="w-full pl-11 pr-4 py-3.5 bg-white/50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-gray-900 placeholder-gray-400 font-medium"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={
                    t("login.email_placeholder") || "name@company.com"
                  }
                  required
                />
              </div>

              {/* Password Field */}
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FiLock className="h-5 w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors" />
                </div>
                <input
                  type="password"
                  className="w-full pl-11 pr-4 py-3.5 bg-white/50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 text-gray-900 placeholder-gray-400 font-medium"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <LoadingButton
              type="submit"
              loading={loading}
              className="w-full bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white py-4 rounded-xl font-bold shadow-lg shadow-primary-600/20 hover:shadow-xl hover:shadow-primary-600/30 transition-all duration-300 transform hover:-translate-y-0.5"
              disabled={!email || !password}
            >
              <span className="text-base">{t("app.signin")}</span>
            </LoadingButton>

            {/* Signup Link and Forgot Password */}
            <div className="flex justify-between items-center text-sm text-gray-500 font-medium">
              <Link
                to="/forgot-password"
                className="text-primary-600 hover:text-primary-700 font-bold hover:underline transition-all"
              >
                {t("login.forgot_password_link", "Forgot your password?")}
              </Link>
              <div>
                {t("login.no_account")}{" "}
                <Link
                  to="/signup"
                  className="text-primary-600 hover:text-primary-700 font-bold hover:underline transition-all"
                >
                  {t("login.signup_link")}
                </Link>
              </div>
            </div>
          </form>
        </div>
      </motion.div>

      <ChangePasswordModal
        isOpen={showChangePasswordModal}
        preventClose={true}
        onClose={() => setShowChangePasswordModal(false)}
        onPasswordChanged={handlePasswordChanged}
      />
    </div>
  );
}
